import React, { useState, useCallback, memo } from 'react';
import { useDispatch } from 'react-redux';
import { object, func, string, bool } from 'prop-types';
import Loader from 'yoda-core-components/lib/components/Loader/Loader';
import {
    useStylitics,
    openMiniPDP,
    styliticsLogger,
} from 'yoda-core-components/lib/helpers/Stylitics';

const StyliticWidget = ({
    config,
    productDetails = {},
    selectedColor = '',
    preferences = {},
    featureFlags = {},
    onOutfitsLoaded = null,
    onJumpImage = null,
    showTitle = true,
    showLoader = true,
}) => {
    const dispatch = useDispatch();
    const [outfits, setOutfits] = useState(null);
    const [loading, setLoading] = useState(true);

    const handleOutfitsLoaded = useCallback(
        (data) => {
            setLoading(false);
            setOutfits(data);
            onOutfitsLoaded?.(data);
            if (config.analyticsLoadAction && data?.length > 0) {
                dispatch(config.analyticsLoadAction(window.digitalData));
            }
        },
        [config, dispatch, onOutfitsLoaded]
    );

    const handleItemClick = useCallback(
        (item, outfit) => {
            const opts = openMiniPDP(item);
            if (opts && config.analyticsClickAction) {
                dispatch(
                    config.analyticsClickAction({
                        id: outfit?.id,
                        ppId: opts.ppId,
                        module: config.analyticsModule,
                    })
                );
            }
            if (config.miniPDPAction) {
                dispatch(config.miniPDPAction(item?.item_id));
            }
        },
        [config, dispatch]
    );

    const handleError = useCallback(
        (err) => {
            setLoading(false);
            console.error(`[Stylitics:${config.id}] Script load failed:`, err);
        },
        [config.id]
    );

    const { containerRef } = useStylitics(config, {
        selectedColor,
        productDetails,
        preferences,
        featureFlags,
        onOutfitsLoaded: handleOutfitsLoaded,
        onItemClick: handleItemClick,
        onError: handleError,
    });

    // Master flag: if false, nothing loads (no SDK, no widget, no network requests)
    if (featureFlags?.enableStylitics === false) return null;

    // Individual flag: each widget can be toggled independently
    if (config.featureFlagKey && featureFlags?.[config.featureFlagKey] === false) {
        styliticsLogger.log('Widget disabled by feature flag:', config.featureFlagKey, config.id);
        return null;
    }

    const hasOutfits =
        outfits && (Array.isArray(outfits) ? outfits.length > 0 : Object.keys(outfits).length > 0);

    return (
        <div>
            {showLoader && loading && <Loader />}
            {showTitle && hasOutfits && config.title && (
                <h4 id={`widget-title-${config.id}`}>{config.title}</h4>
            )}
            <div id={config.containerId} ref={containerRef} />
        </div>
    );
};

StyliticWidget.propTypes = {
    config: object.isRequired,
    productDetails: object,
    selectedColor: string,
    preferences: object,
    featureFlags: object,
    onOutfitsLoaded: func,
    onJumpImage: func,
    showTitle: bool,
    showLoader: bool,
};

export default memo(StyliticWidget);
